<template>
  <div :class="ui.root">
    <AwCheckbox v-model="isDone" class="shrink-0" @click.stop />

    <!-- Outer layer for layout -->
    <div :class="ui.outerLayer">
      <!-- Inner layer for strike through -->
      <div :class="[ui.strikeTarget, isDone && ui.strikeDone]">
        <AwBadge
          :label="dueDateLabel"
          :color="displayedDueDateColor"
          leading-icon="i-ph-calendar-blank"
          size="sm"
          variant="light"
          :class="ui.badge"
        />
        <AwBadge
          v-if="action"
          :label="action.label"
          :color="displayedActionColor"
          :leading-icon="action.icon"
          size="sm"
          variant="light"
          :class="ui.badge"
        />
        <TaskContextBadge
          v-for="subject in subjects"
          :key="`${subject.entity}-${subject.entity_id}`"
          :subject="subject"
          :is-done="isDone"
        />
        <slot name="context"></slot>
        <span :class="[ui.name, isDone && ui.nameDone]">{{ name }}</span>
      </div>
    </div>

    <div :class="ui.meta">
      <AwTooltip v-if="createdByName" :text="$t('tasks.item.assignedBy', { name: createdByName })">
        <AwAvatar :text="createdByName" size="xxs" />
      </AwTooltip>
      <span :class="ui.createdAt">{{ createdAtLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AwAvatar, AwBadge, AwCheckbox, AwTooltip } from "@asap/design";
import type { TaskAction } from "@asap/shared/schemas/task/action-type";
import type { TaskSubject } from "@asap/shared/schemas/task/context";
import { getFullName } from "@asap/shared/utils/people";
import TaskContextBadge from "~/components/task/TaskContextBadge.vue";
import { getTaskActionLabels, TASK_ACTION_CATALOG } from "~/utils/task-action";
import { formatTaskCreatedAt, formatTaskDueDateLabel, getTaskDueDateColor } from "~/components/task/task-date";

const props = withDefaults(
  defineProps<{
    name: string | null;
    dueDate: string;
    actionType?: TaskAction | null;
    createdAt: string | Date;
    createdBy?: { first_name: string | null; last_name: string | null } | null;
    subjects?: TaskSubject[];
  }>(),
  {
    actionType: null,
    createdBy: null,
    subjects: () => [],
  }
);

const isDone = defineModel<boolean>("isDone", { default: false });
const { t } = useI18n();

const createdByName = computed(() => (props.createdBy ? getFullName(props.createdBy) : null));
const createdAtLabel = computed(() => formatTaskCreatedAt(props.createdAt));
const dueDateLabel = computed(() => formatTaskDueDateLabel(props.dueDate, t));
const dueDateColor = computed(() => getTaskDueDateColor(props.dueDate));
const taskActionLabels = getTaskActionLabels(t);
const action = computed(() => {
  if (!props.actionType) {
    return null;
  }

  return {
    ...TASK_ACTION_CATALOG[props.actionType],
    label: taskActionLabels[props.actionType],
  };
});
const displayedDueDateColor = computed(() => (isDone.value ? "secondary" : dueDateColor.value));
const displayedActionColor = computed(() => (isDone.value ? "secondary" : (action.value?.color ?? "secondary")));

const ui = computed(() => ({
  root: "flex items-center gap-2 px-3 py-2 min-h-10",
  outerLayer: "flex min-w-0 flex-1 items-center overflow-hidden",
  badge: ["shrink-0", isDone.value && "text-gray!"],
  name: "min-w-0 truncate text-sm ",
  nameDone: "text-secondary-600",
  meta: "flex items-center gap-2 shrink-0 ml-auto",
  createdAt: "text-xs text-secondary-600 shrink-0",
  strikeTarget: [
    "relative flex w-fit max-w-full items-center gap-2 min-w-0",
    "after:pointer-events-none",
    "after:absolute after:left-0 after:top-1/2 after:-translate-y-1/2",
    "after:h-2px after:w-full",
    "after:rounded-md after:bg-secondary-600",
    "after:content-['']",
    "after:origin-left after:scale-x-0",
    "after:transition-transform after:duration-200 after:ease-in-out",
  ],
  strikeDone: ["after:scale-x-100"],
}));
</script>
